import { Formik, FormikProps } from "formik";
import React, { useEffect, useRef, useState } from "react";
import { DASHBOARD_ROOT } from "../../components/Routes/routingConstants";
import { useMutation } from "@apollo/client";
import toast from "react-hot-toast";
import PromptDialogs from "../../components/PromptDialog";
import { addCategory, removeCategory, updateCategory } from "../../graphql/content-management/addCategories";
import { useCategoriesEmpowerMe } from "../../hooks/useCategories"
import Category from "../../interfaces/models/Category";
import useImageUpload from "../../hooks/useImageUpload";
import { executeDeleteImage } from "../../graphql/queries/getImageUploadURL";
import Breadcrumbs from "../../components/Breadcrumbs/Breadcrumbs";
import { Alert, Button, Col, Form, Modal, ModalBody, ModalFooter, ModalHeader, Row, Table } from "reactstrap";
import Loading from "../../components/Loading";
import ImageUploadInput from "../../components/ContentManagement/ImageUploadInput";
import TextFormInput from "../../components/Shared/Forms/TextFormInput";
import AddCategoryValidator from "../../validators/AddCategoryValidator";
import { CategoryTypes } from "../../helper/constants";
import ToggleableImage from "../../components/Shared/ToggleableImage"


//#region Interfaces 
interface ModalLabels {
    name : string;
    buttonName : string;
    action : string;
}

interface CategoryFormik {
    _id: string;
    description?: string;
    image?: string;
    image_name?: string;
    level: number;
    parent_id?: string;
    color?: string;
}

interface HookReturn {
    ok : boolean;
    data : string;
}
//#endregion Intefaces

const EmpowerMeCategoriesEditorPage = () => {
    
    //#region Initializations
    const breadcrumbsData = [
        { name: 'Content List', path: `${DASHBOARD_ROOT}/content-management/` },
        { name: 'Edit Empower Me Categories', path: `${DASHBOARD_ROOT}/content-management/empower-me/categories` },
    ];

    const [categories, setCategories] = useState<Category[]>([]);
    const [subCategories, setSubCategories] = useState<Category[]>([]);

    const { data, loading, error } = useCategoriesEmpowerMe();

    const formikRef = useRef<FormikProps<CategoryFormik>>(null);
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [isOnProcess, setIsOnProcess] = useState<boolean>(false);
    const [selectedValue, SetSelectedValue] = useState<CategoryFormik>();
    const [modalLabels, setModalLabels] = useState<ModalLabels>({
        name : "",
        buttonName : "",
        action : ""
    });
    
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);

    const [blobFile, setBlobFile] = useState<File>(null);
    const [url, setUrl] = useState<string>("");
    const [fileId, setFileId] = useState<string>("");
    const imageInputRef = useRef<{ clearImageField: () => void, refetchUrl: () => void }>(null);

    const [addCategoryHook] = useMutation(addCategory,{
        onCompleted : () =>{

        },
        onError : () =>{
            toast.error("Something went wrong whilst adding Category. Please try again or if issue persists, please contact us");
        }
    });
    const [updateCategoryHook] = useMutation(updateCategory,{
        onCompleted : () =>{

        },
        onError : () =>{
            toast.error("Something went wrong whilst updating Category . Please try again or if issue persists, please contact us");
        }
    });
    const [removeCategoryHook] = useMutation(removeCategory,{
        onCompleted : () =>{

        },
        onError : (error) =>{
            toast.error(error.graphQLErrors[0].message);
        }
    });
    
    const { uploadImage } = useImageUpload();
    const [deleteFile] = useMutation(executeDeleteImage);

    const Reloader = () =>{
        setTimeout(function() {
            window.location.reload();
        },1500)
    }

    const DeleteFileExecute = async (fileIdCloud : string) => {
        if(!fileIdCloud){
            return; 
        }
        try{
            await deleteFile({
                variables : {
                    fileId : fileIdCloud
                },
            })
        }
        catch(err){
            console.log(err);
        }
    }

    const HandleImageChange = (url : string, fileId : string, blob : File) => {
        setUrl(url);
        setFileId(fileId);
        setBlobFile(blob);
    }

    const IsFetchingToggle = (isGetting : boolean) =>{
        setIsOnProcess(isGetting);
    }

    const TriggerSubmit = () => {
        formikRef.current?.submitForm();
    }

    const ToggleModal = () => {
        if(isModalOpen){
            imageInputRef.current?.clearImageField();
        }
        setIsModalOpen(!isModalOpen);
        
    }

    useEffect(() => {
        if(data) {
            const categRaw : Category[] = data.categories_empower_me; 
            let mains : Category[] = [];
            let subs : Category[] = []
            for(let x = 0; x < categRaw.length; x++) {
                const getCategRaw = categRaw[x];

                if(getCategRaw.level === 2) {
                    mains.push(getCategRaw);
                }
                else {
                    subs.push(getCategRaw);
                }

            }
            setCategories(mains);
            setSubCategories(subs);
        }
    }, [data]);

    //#endregion Initializations

    //#region Add and Edit Functions
    const HandleOpenAdd = (parent : Category | null) => {
        if(parent) {
            SetSelectedValue({
                _id : "1",
                description : "",
                image : "",
                image_name : "",
                level : parent.level + 1,
                parent_id : parent._id
            })
        }
        else {
            //64cad38bbe1db700621382c7
            SetSelectedValue({
                _id : "1",
                description : "",
                image : "",
                image_name : "",
                level : 2,
                parent_id : "64cad38bbe1db700621382c7", // static id for parent of empower me level 2 categories
            })
        }
        imageInputRef.current?.clearImageField();
        setModalLabels({
            name : "Add Category",
            buttonName : "Add",
            action : "add"
        })
        ToggleModal();
    }

    const HandleOpenEdit = (item : Category) => {
        SetSelectedValue({
            _id : item._id,
            description : item.description,
            image_name : "myImage.jpeg",
            image : item.image,
            level : item.level
        })
        setModalLabels({
            name : "Edit Category",
            buttonName : "Edit",
            action : "edit"
        })
        imageInputRef.current?.clearImageField();
        ToggleModal();
    }

    const HandleSubmit = async (formData : CategoryFormik) => {
        
        if(isOnProcess) return;

        setIsOnProcess(true);
        if(blobFile){
            const isUploaded = await uploadImage(url,blobFile);
            if(!isUploaded){
                toast.error("Something went wrong whilst uploading your image. Please try again or if issue persists, please contact us");
                
                setIsOnProcess(false);    
                return;
            }
        }

        if(modalLabels.action === "add") {

            const createCategory : HookReturn = await addCategoryHook({
                variables : {
                    record :{
                        description : formData.description,
                        image : fileId,
                        level : formData.level,
                        type : CategoryTypes.EMPOWER_ME,
                        parent_id : formData.parent_id
                    }
                }
            }).then((res)=>res.errors ? {ok : false, data : null} : {ok : true, data : res.data.category_CreateOne.record._id}).catch(()=> {
                return {ok : false, data : null}
            });

            if(!createCategory.ok) {
                setIsOnProcess(false);
                await DeleteFileExecute(fileId);
                imageInputRef.current.refetchUrl();
                return;
            }

            Reloader();

        }
        else if (modalLabels.action === "edit"){
            
            //update in category table 
            const updateCategory = await updateCategoryHook({
                variables : {
                    _id : selectedValue._id,
                    record :{
                        description : formData.description,
                        image : fileId || selectedValue.image,
                        level : formData.level,
                        parent_id : formData.parent_id
                    }
                }
            }).then((res)=>res.errors ? false : true).catch(()=> false);

            if(!updateCategory) {
                setIsOnProcess(false);
                await DeleteFileExecute(fileId);
                imageInputRef.current.refetchUrl();
                return;
            }

            if(fileId) {
                await DeleteFileExecute(selectedValue.image);
            }

            Reloader();
        }
    }

//#endregion Add and Edit Functions
    //#region Delete Functions
    const HandleOpenDelete = (item : Category) => { 
        SetSelectedValue(item);
        setIsDeleteModalOpen(true);
    }

    const HandleDelete = async () => {
        if(isOnProcess) return;

        setIsOnProcess(true);
        const isSuccess = await removeCategoryHook({
            variables : {
                _id : selectedValue._id,
            }
        }).then((res) => res.errors ? false : true).catch(()=>false);

        if(!isSuccess) {
            setIsOnProcess(false);
            return;
        }
        await DeleteFileExecute(selectedValue.image);

        Reloader(); 
    }
    //#endregion Delete Functions
        
    return (
        <>
            <Breadcrumbs 
                crumbs={breadcrumbsData}
            />
            {
                error &&
                <Alert color="danger">
                    Oops, we’re having trouble getting the Categories. Please give it a minute, and if it doesn’t resolve, let us know.
                </Alert>
            }
            <Row className="pb-3">
                <Col md={8}></Col>
                <Col md={4}>
                    <div className="d-flex justify-content-end">
                        <Button color="primary" onClick={() => { HandleOpenAdd(null) }}>Add</Button>
                    </div>
                </Col>
            </Row>
            <div className="table-responsive ">
                <Table bordered className="bg-white ">
                    <thead>
                        <tr>
                            <td>Image</td>
                            <td>Name</td>
                            <td>Action </td>
                        </tr>
                    </thead>
                    <tbody>
                        {
                            loading === false && categories.length === 0 &&
                            <tr>
                                <td colSpan={3} className="text center">No Empower Me Categories Found</td>
                            </tr> 
                        }
                        {
                            loading  ?
                            <tr>
                                <td colSpan={3}>
                                    <Loading />
                                </td>
                            </tr>
                            :
                            categories.map((item, index) => {
                                const childCategories = subCategories.filter(u => u.parent_id === item._id);
                                return (
                                    <React.Fragment key={index}>
                                        <tr className="bg-light">
                                            <td className="text-center">
                                                {
                                                    item.image && <ToggleableImage image={item.image} />
                                                }
                                            </td>
                                            <td>
                                                <div>{item.description}</div>
                                            </td>
                                            <td>
                                                <Button color="primary" size="sm" onClick={() => { HandleOpenEdit(item) }}>Edit</Button>
                                                <Button color="primary" size="sm" onClick={() => { HandleOpenAdd(item) }}> Add Sub Category</Button>
                                                <Button color="danger" size="sm" onClick={() => { HandleOpenDelete(item) }}> Delete</Button>
                                            </td>
                                        </tr>
                                        {
                                            childCategories.length === 0 &&
                                            <tr>
                                                <td colSpan={3} className="text-center">No Sub Category Found</td>
                                            </tr>
                                        }
                                        {
                                            childCategories.map((subItem, subIndex) => {

                                                return (
                                                    <tr key={subIndex}>
                                                        <td className="text-center">
                                                            {
                                                                subItem.image && <ToggleableImage image={subItem.image} />
                                                            }
                                                        </td>
                                                        <td className="pl-5">
                                                            <div>{subItem.description}</div>
                                                        </td>
                                                        <td>
                                                            <Button color="primary" size="sm" onClick={() => { HandleOpenEdit(subItem) }}>Edit</Button>
                                                            <Button color="danger" size="sm" onClick={() => { HandleOpenDelete(subItem) }}> Delete</Button>
                                                        </td>
                                                    </tr> 
                                                )
                                            })
                                        }
                                    </React.Fragment>
                                )
                            })
                        }
                    </tbody>
                </Table>
            </div>
            <Modal
                id="em-category-edit-modal"
                className="modal-lg"
                backdrop={true}
                isOpen={isModalOpen}>
                <ModalHeader toggle={ToggleModal} className="pb-0">
                    <p className="text-lg">{modalLabels.name}</p>
                </ModalHeader>
                <ModalBody>
                    <Formik
                        innerRef={formikRef}
                        initialValues={selectedValue}
                        validationSchema={AddCategoryValidator}
                        onSubmit={async (value : CategoryFormik) =>{
                            await HandleSubmit(value);
                        }}
                    >
                        {({touched, errors, values}) => (
                            <Form>
                                <TextFormInput
                                    name={"description"}
                                    placeholder="Enter Name"
                                    label="Description"
                                    required={true}
                                    className="mb-0"
                                />
                                <ImageUploadInput
                                    required={true}
                                    ref={imageInputRef}
                                    formikRef={formikRef}
                                    transferFunction={HandleImageChange}
                                    isGettingUrl={IsFetchingToggle}
                                    imageName="image_name"
                                    imageIdName="image"
                                />
                            </Form>
                        )}
                    </Formik>
                    
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" disabled={isOnProcess} onClick={ToggleModal} >Close </Button>
                    <Button color="primary" disabled={isOnProcess} onClick={TriggerSubmit} >{modalLabels.buttonName}</Button>
                </ModalFooter>
            </Modal>

            <PromptDialogs
                isOpen={isDeleteModalOpen}
                onCancel={() => { setIsDeleteModalOpen(false); }}
                onConfirm={HandleDelete}
                title="Delete Category"
                promptMessage="Are you sure you want to delete this Category?"
                confirmBtnTxt="Delete"
                isOnProcess={isOnProcess}
            />
        </>
    )
}
export { EmpowerMeCategoriesEditorPage as EmpowerMeCategoriesEditor };